import { useQuery } from '@tanstack/react-query';
import { countryConfig } from '../../country';
import { LocaleLink as Link } from '../../i18n/locale-link';
import { Seo } from '../../components/seo';
import { useLocale, useT } from '../../i18n/locale-context';
import { usePageTitle } from '../../lib/page-title';
import { fetchCategoryHubs, fetchRegions } from '../../lib/catalog-client';
import { localePath } from '../../i18n/locale-path';

const SITE_ORIGIN = countryConfig.brand.siteUrl;

/**
 * The hub-of-hubs.
 *
 * WHY THIS PAGE EXISTS. Measured on prod 2026-08-12: the site had 31 category
 * hubs and 14 region hubs, and **43 of those 45 were orphaned**. The homepage
 * linked exactly two categories (curated examples in the guest sidebar) and zero
 * regions; nothing else on the site linked a region hub except other region pages
 * and the tender pages that happen to belong to it. There was no index of either.
 * So the pages targeting «Тендеры и госзакупки в <регион>» — the most winnable
 * commercial head terms this catalog has — were reachable only from sitemap.xml,
 * which is the profile that produces "Discovered — currently not indexed".
 *
 * WHY A ROUTE AND NOT A FOOTER BLOCK. A 45-link footer would put every hub one hop
 * from every page, which is more link signal. But the footer renders on all ~22.5k
 * pages, and to stay accurate it would have to either fetch (a cache MISS on every
 * prerendered page → an EMPTY footer in the HTML that actually matters, i.e. the
 * fix silently does nothing) or hardcode 45 slugs and names in two locales (which
 * goes stale the first time a category is added). This route is seeded by the
 * prerender exactly like /prices, so its links are real HTML, always current, and
 * every hub sits two hops from anywhere via the footer link.
 */
export function Directory() {
  const t = useT();
  const { locale } = useLocale();
  usePageTitle(t('directory.title'));

  const { data: categories } = useQuery({
    // Locale is in the key: names are localised server-side.
    queryKey: ['directory-categories', locale],
    queryFn: () => fetchCategoryHubs(locale),
  });
  const { data: regions } = useQuery({
    queryKey: ['directory-regions', locale],
    queryFn: () => fetchRegions(locale),
  });

  // Deliberately `fetchCategoryHubs`, NOT `fetchCategories`: the latter applies the
  // IT grouping, whose synthetic `it-telecom,telecom-equipment` slug is a filter
  // value and not a route, and which REMOVES both real member hubs. See the note on
  // fetchCategoryHubs. A comma in a slug here would also mean the prerendered HTML
  // and the post-hydration client disagreed about which links exist.
  const categoryLinks = categories ?? [];

  const sections: Array<{
    heading: string;
    intro: string;
    items: Array<{ to: string; name: string; count: number }>;
  }> = [
    {
      heading: t('directory.categories.h2'),
      intro: t('directory.categories.intro'),
      items: categoryLinks.map((c) => ({
        to: `/category/${c.slug}`,
        name: c.name,
        count: c.count,
      })),
    },
    {
      heading: t('directory.regions.h2'),
      intro: t('directory.regions.intro'),
      items: (regions ?? []).map((r) => ({
        to: `/region/${r.slug}`,
        name: r.name,
        count: r.count,
      })),
    },
  ];

  return (
    <>
      <Seo
        title={t('directory.seoTitle')}
        description={t('directory.seoDesc')}
        canonical="/directory"
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'CollectionPage',
              name: t('directory.seoTitle'),
              description: t('directory.seoDesc'),
              url: `${SITE_ORIGIN}${localePath('/directory', locale)}`,
              inLanguage: locale,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: t('nav.catalog'),
                  item: `${SITE_ORIGIN}${localePath('/', locale)}`,
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: t('directory.title'),
                  item: `${SITE_ORIGIN}${localePath('/directory', locale)}`,
                },
              ],
            },
          ],
        }}
      />
      <div className="mx-auto max-w-3xl">
        <h1 className="text-[22px] font-bold leading-tight text-ink">{t('directory.title')}</h1>
        <p className="mt-2 text-[14px] leading-[1.6] text-ink-muted">{t('directory.intro')}</p>

        <Link
          to="/companies"
          className="mt-6 block rounded-lg border border-edge bg-bone-surface px-4 py-3 transition-colors hover:border-brand"
        >
          <span className="block text-[15px] font-bold text-ink">
            {t('directory.companies.h2')}
          </span>
          <span className="mt-0.5 block text-[13px] leading-[1.5] text-ink-muted">
            {t('directory.companies.intro')}
          </span>
        </Link>

        {sections.map((section) => (
          <section key={section.heading} className="mt-8">
            <h2 className="text-[16px] font-bold text-ink">{section.heading}</h2>
            <p className="mt-1 text-[13px] leading-[1.6] text-ink-muted">{section.intro}</p>
            <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-0.5 sm:grid-cols-2">
              {section.items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="flex items-baseline justify-between gap-3 border-b border-edge-light py-2 text-[14px] transition-colors hover:text-brand"
                  >
                    <span className="font-medium text-ink">{item.name}</span>
                    <span className="shrink-0 text-[12px] tabular-nums text-ink-muted">
                      {item.count}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}
